import React, { useEffect, useState } from 'react';
import {
    attestationNumber,
    attestationToken,
    certificateTitle,
    coverStamp,
    formatCertDate,
    plateOf,
    qrImageUrl,
    securityKeyFromToken,
    verifyUrl
} from '../attestation';
import { TENANT_COMPANY_NAME } from '../tenant';
import { brandMark, useI18n } from '../i18n';

function vinOf(p) {
    return (p.vehicle && (p.vehicle.vin || p.vehicle.VIN || p.vehicle.chassisNumber)) || '—';
}

function vehicleLine(p) {
    const v = p.vehicle || {};
    return [v.model, v.year].filter(Boolean).join(' · ') || '—';
}

function InsuranceAttestation({ companyName, logoUrl, customer, policy, tenantId, verifyMode }) {
    const { t } = useI18n();
    const [token, setToken] = useState('');
    const stamp = coverStamp(policy);
    const plate = plateOf(policy);
    const attNo = attestationNumber(policy);
    const issued = formatCertDate(policy.effectiveDate || policy.createdAt, true);
    const from = formatCertDate(policy.effectiveDate);
    const until = formatCertDate(policy.expiryDate) + ' at 23:59';
    const title = certificateTitle(policy);
    const isMotor = title.indexOf('MOTOR') !== -1;
    const insurer = companyName || TENANT_COMPANY_NAME;
    const mark = brandMark(insurer);
    const stampLabel = stamp.kind === 'ok' ? t('cert.valid') : stamp.kind === 'expired' ? t('cert.expired') : stamp.label;

    useEffect(() => {
        let live = true;
        attestationToken(tenantId || 'igi_insurance', policy.id).then((tok) => {
            if (live) setToken(tok);
        });
        return () => { live = false; };
    }, [tenantId, policy.id]);

    const url = token ? verifyUrl(token) : '';

    return (
        <div className={'att-page att-' + stamp.kind}>
            <div className="att-wm" aria-hidden="true">
                {Array.from({ length: 36 }).map((_, i) => (
                    <span key={i}>{t('cert.gambiaBand', { mark })}</span>
                ))}
            </div>
            <div className="att-header">
                <div className="att-brand">
                    <div className="att-crest" aria-hidden="true">{mark}</div>
                    <div>
                        <div className="att-org">{t('cert.org')}</div>
                        <h1>{title}</h1>
                        <div className="att-sub">{t('cert.official')}</div>
                    </div>
                </div>
                <div className="att-header-right">
                    <div className="att-lock" title="Security · Availability · Proximity">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <rect x="5" y="11" width="14" height="10" rx="2" stroke="#1d4ed8" strokeWidth="2" />
                            <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="#1d4ed8" strokeWidth="2" />
                        </svg>
                        <span>SECURITY<br />AVAILABILITY<br />PROXIMITY</span>
                    </div>
                    <div className="att-platebox">
                        <div><span>{t('cert.plateNo')}</span><strong>{plate}</strong></div>
                        <div><span>{t('cert.type')}</span><strong>{policy.vehicle && policy.vehicle.use ? String(policy.vehicle.use).toUpperCase() : (isMotor ? 'PRIVATE VEHICLE' : (policy.policyType || 'COVER').toString().toUpperCase())}</strong></div>
                        <div><span>{t('cert.make')}</span><strong>{(policy.vehicle && policy.vehicle.make) || '—'}</strong></div>
                    </div>
                </div>
            </div>

            <div className="att-banner">
                <div className="att-insurer">
                    {logoUrl ? <img src={logoUrl} alt="" /> : <div className="att-logo-fallback">{mark}</div>}
                    <div>
                        <strong>{insurer}</strong>
                        <div className="att-contact">{t('cert.contact')}</div>
                    </div>
                </div>
                <div className={'att-status att-status-' + stamp.kind}>{stampLabel}</div>
            </div>

            <div className="att-body">
                <div className="att-details">
                    <div className="att-row"><span>{t('cert.holder')}</span><strong>{customer.fullName}</strong></div>
                    <div className="att-row"><span>{t('cert.nationalId')}</span><strong>{customer.nationalId || '—'}</strong></div>
                    {isMotor ? (
                        <div className="att-row"><span>{t('cert.vehicle')}</span><strong>{vehicleLine(policy)}</strong></div>
                    ) : null}
                    {isMotor ? (
                        <div className="att-row"><span>{t('cert.vin')}</span><strong className="att-mono">{vinOf(policy)}</strong></div>
                    ) : null}
                    <div className="att-row"><span>{t('cert.policyNo')}</span><strong>{policy.policyNumber}</strong></div>
                    <div className="att-row"><span>{t('cert.validFrom')}</span><strong>{from} {t('cert.to')} {until}</strong></div>
                    <div className="att-attno">{t('cert.attestationNo', { num: attNo })}</div>
                    <div className="att-row"><span>{t('cert.securityKey')}</span><strong className="att-mono">{token ? securityKeyFromToken(token) : '…'}</strong></div>
                    <div className="att-row"><span>{t('cert.cover')}</span><strong>{policy.productName || policy.coverageType || policy.policyType}</strong></div>
                    {policy.ecowasBrownCard ? <div className="att-row"><span>{t('cert.ecowas')}</span><strong>{t('cert.brownCard')}</strong></div> : null}
                </div>

                <div className="att-pass">
                    <div className="att-pass-date">{t('cert.dateOfIssue', { date: issued })}</div>
                    <div className="att-pass-inner">
                        {logoUrl ? <img className="att-pass-logo" src={logoUrl} alt="" /> : <div className="att-pass-name">{insurer}</div>}
                        <div className="att-pass-bar">{policy.policyNumber}</div>
                        <div className="att-pass-red">{attNo}</div>
                        <div className="att-pass-title">{t('cert.attestationTitle')}</div>
                        {verifyMode ? (
                            <div className={'att-verify-mark att-verify-' + stamp.kind}>
                                {stamp.kind === 'ok' ? t('cert.coverConfirmed') : stampLabel}
                            </div>
                        ) : url ? (
                            <img className="att-qr" src={qrImageUrl(url)} alt="Police verification QR" />
                        ) : (
                            <div className="att-qr-wait">{t('cert.preparingQr')}</div>
                        )}
                        <div className="att-pass-foot">{t('cert.plate', { plate })}</div>
                        <div className="att-pass-foot">{t('cert.expires', { date: until })}</div>
                    </div>
                </div>
            </div>
            {stamp.kind === 'expired' ? <div className="att-expired-stamp">{t('cert.expired')}</div> : null}
            <div className="att-watermark">{t('cert.watermark', { company: insurer })}</div>
        </div>
    );
}

export default InsuranceAttestation;
